import React, { useState } from 'react';
import { X, Clock } from 'lucide-react';
import { completeService } from '../services/appointmentApi';
import { useI18n } from '../contexts/I18nContext';

interface CompleteServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentId: string;
  estimatedDuration: number; // Tempo estimado em minutos
  onSuccess: () => void;
}

const CompleteServiceModal: React.FC<CompleteServiceModalProps> = ({
  isOpen,
  onClose,
  appointmentId,
  estimatedDuration,
  onSuccess
}) => {
  const { t } = useI18n();
  const [actualDuration, setActualDuration] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Log para debug
      console.log('🔍 CompleteService - appointmentId recebido:', appointmentId, 'Tipo:', typeof appointmentId);
      
      // Validar se appointmentId existe e não está vazio
      if (!appointmentId || appointmentId.trim() === '') {
        console.error('❌ appointmentId está vazio ou inválido');
        throw new Error('ID de agendamento não encontrado. Por favor, recarregue a página e tente novamente.');
      }

      // Validar se appointmentId é um UUID válido (formato: 8-4-4-4-12)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(appointmentId.trim())) {
        console.error('❌ appointmentId não é um UUID válido:', appointmentId);
        // Tentar enviar mesmo assim - o backend pode aceitar outros formatos
        console.warn('⚠️ Tentando enviar mesmo assim - o backend validará...');
      } else {
        console.log('✅ appointmentId é um UUID válido');
      }

      const data: any = {};
      
      // Se informou tempo real, usa ele; senão usa o estimado
      if (actualDuration && actualDuration !== '') {
        const parsed = parseInt(actualDuration);
        if (isNaN(parsed) || parsed <= 0) {
          throw new Error('Tempo real deve ser um número positivo');
        }
        data.actual_duration = parsed;
      }

      // Se informou hora de término, usa ela
      if (endTime && endTime !== '') {
        // Converter para RFC3339
        const date = new Date(endTime);
        if (isNaN(date.getTime())) {
          throw new Error('Data/hora de término inválida');
        }
        data.end_time = date.toISOString();
      }

      if (notes.trim() !== '') {
        data.notes = notes.trim();
      }

      await completeService(appointmentId, data);
      
      // Limpar formulário
      setActualDuration('');
      setEndTime('');
      setNotes('');
      
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Erro ao finalizar serviço:', err);
      console.error('Erro completo:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      
      let errorMessage = 'Erro ao finalizar serviço';
      
      if (err.response) {
        // Erro com resposta do servidor
        if (err.response.status === 400) {
          errorMessage = err.response.data?.error || 'Dados inválidos. Verifique os campos preenchidos.';
        } else if (err.response.status === 404) {
          errorMessage = 'Agendamento não encontrado. Por favor, recarregue a página.';
        } else if (err.response.status === 500) {
          errorMessage = err.response.data?.error || 'Erro interno do servidor. Tente novamente.';
        } else {
          errorMessage = err.response.data?.error || err.message || 'Erro ao finalizar serviço';
        }
      } else if (err.request) {
        // Erro de rede (sem resposta)
        errorMessage = 'Erro de conexão. Verifique sua conexão com a internet.';
      } else {
        // Outro tipo de erro (incluindo validação)
        errorMessage = err.message || 'Erro ao finalizar serviço';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 3000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        width: '450px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>{t('completeService.title')}</h3>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <X size={20} color="#666" />
          </button>
        </div>

        <div style={{
          backgroundColor: '#f3f4f6',
          padding: '12px',
          borderRadius: '6px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Clock size={16} color="#666" />
            <span style={{ fontSize: '14px', color: '#666', fontWeight: '500' }}>
              Tempo Estimado:
            </span>
            <span style={{ fontSize: '14px', color: '#333', fontWeight: '600' }}>
              {formatDuration(estimatedDuration)}
            </span>
          </div>
          <p style={{
            fontSize: '12px',
            color: '#666',
            margin: 0,
            fontStyle: 'italic'
          }}>
            Se não informar o tempo real, será usado o tempo estimado ({formatDuration(estimatedDuration)})
          </p>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
              Tempo Real (minutos) <span style={{ color: '#666', fontWeight: 'normal', fontSize: '12px' }}>{t('common.optional')}</span>
            </label>
            <input
              type="number"
              min="1"
              value={actualDuration}
              onChange={(e) => setActualDuration(e.target.value)}
              placeholder={`Ex: ${estimatedDuration} (ou deixe em branco para usar estimado)`}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                outline: 'none',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
              Hora de Término <span style={{ color: '#666', fontWeight: 'normal', fontSize: '12px' }}>{t('common.optional')}</span>
            </label>
            <input
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                outline: 'none',
                fontSize: '14px'
              }}
            />
            <p style={{ fontSize: '12px', color: '#666', marginTop: '4px', margin: 0 }}>{t('completeService.autoCalcHint')}</p>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
              Notas <span style={{ color: '#666', fontWeight: 'normal', fontSize: '12px' }}>{t('common.optional')}</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observações sobre a finalização do serviço..."
              rows={3}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                outline: 'none',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                flex: 1,
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                padding: '10px',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              {isLoading ? 'Finalizando...' : 'Finalizar Serviço'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                flex: 1,
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '10px',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >{t('actions.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompleteServiceModal;

